import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  CircularProgress,
  Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { subDays, format } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { fetchTimeRangeData } from "../../../../api/overView";
import { getCalendarData } from "api/tableView";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { useParams } from "react-router";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded shadow-sm">
        <p className="font-medium text-sm mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-600">{entry.name}:</span>
            <span className="font-medium">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const baseStyles = {
  borderRadius: '50%',
  transition: 'background-color 0.2s ease-in-out',
  '&:not(.Mui-selected)': {
    border: 'none',
  }
};

const colorStyles = {
  selected: {
    ...baseStyles,
    backgroundColor: '#1976d2 !important',
    color: 'white !important',
    '&:hover': {
      backgroundColor: '#1565c0 !important',
    }
  },
  zero: {
    ...baseStyles,
    backgroundColor: '#4caf50 !important',
    color: 'white !important',
    '&:hover': {
      backgroundColor: '#43a047 !important',
    }
  },
  low: {
    ...baseStyles,
    backgroundColor: '#ff9800 !important',
    color: 'white !important',
    '&:hover': {
      backgroundColor: '#f57c00 !important',
    }
  },
  high: {
    ...baseStyles,
    backgroundColor: '#f44336 !important',
    color: 'white !important',
    '&:hover': {
      backgroundColor: '#d32f2f !important',
    }
  },
  default: baseStyles
};

const ServerDay = React.memo(({ highlightedDays = [], day, outsideCurrentMonth, selected, ...other }) => {
  const daysArray = Array.isArray(highlightedDays) ? highlightedDays : [];
  console.log('ServerDay highlightedDays:', daysArray);
  
  const formattedDay = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
  console.log('Formatted Day:', formattedDay);
  
  const dayData = daysArray.find(d => {
    if (!d || !d.date) return false;
    const apiDate = new Date(d.date);
    const formattedApiDate = `${apiDate.getFullYear()}-${String(apiDate.getMonth() + 1).padStart(2, '0')}-${String(apiDate.getDate()).padStart(2, '0')}`;
    console.log('Comparing:', formattedApiDate, formattedDay);
    return formattedApiDate === formattedDay;
  });
  
  console.log('Day Data:', dayData);

  const getStyles = () => {
    if (selected) {
      console.log('Selected style');
      return colorStyles.selected;
    }
    if (!dayData) {
      console.log('Default style');
      return colorStyles.default;
    }

    const count = dayData.count || 0;
    console.log('Count:', count);
    
    if (count === 0) {
      console.log('Zero style');
      return colorStyles.zero;
    }
    if (count < 5) {
      console.log('Low style');
      return colorStyles.low;
    }
    console.log('High style');
    return colorStyles.high;
  };

  const styles = getStyles();
  console.log('Applied styles:', styles);

  return (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      selected={selected}
      sx={styles}
    />
  );
});

const ExpiryTrendChart = () => {
  const selectedDate = useSelector((state) => state.customization.selectedDate);
  const [startDate, setStartDate] = useState(subDays(selectedDate || new Date(), 7));
  const [endDate, setEndDate] = useState(selectedDate || new Date());
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalExpired, setTotalExpired] = useState(0);
  const [totalNearExpiry, setTotalNearExpiry] = useState(0);
  const [highlightedDays, setHighlightedDays] = useState([]);
  const abortControllerRef = useRef(null);
  const { storeId } = useParams(); 
  // Update dates when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      setEndDate(selectedDate);
      setStartDate(subDays(selectedDate, 7));
    }
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      setLoading(true);

      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      
      const response = await fetchTimeRangeData(
        formattedStartDate, 
        formattedEndDate,
        storeId,
        abortControllerRef.current.signal
      );
      
      setChartData(response.data.map(item => ({
        date: format(new Date(item.range), 'dd/MM'),
        expired: item.expired_products_count,
        nearExpiry: item.nearly_expired_count
      })));

      // Calculate totals
      const expiredTotal = response.data.reduce((sum, item) => sum + item.expired_products_count, 0);
      const nearExpiryTotal = response.data.reduce((sum, item) => sum + item.nearly_expired_count, 0);
      setTotalExpired(expiredTotal);
      setTotalNearExpiry(nearExpiryTotal);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        console.error('Error fetching data:', error);
      }
    } finally {
      setLoading(false);
      if (abortControllerRef.current) {
        abortControllerRef.current = null;
      }
    }
  };

  const fetchHighlightedDays = async (targetDate) => {
    try {
      const response = await getCalendarData({
        year: targetDate.getFullYear(),
        month: targetDate.getMonth() + 1,
        source_id: storeId
        // source_id: "S002"
      });
      console.log('Calendar API Response:', response);
      
      if (response?.data?.data) {
        setHighlightedDays(response.data.data);
      } else {
        setHighlightedDays([]);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setHighlightedDays([]);
    }
  };

  useEffect(() => {
    fetchData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line
  }, [startDate, endDate]);

  useEffect(() => {
    fetchHighlightedDays(startDate);
    // eslint-disable-next-line
  }, [startDate]);

  useEffect(() => {
    fetchHighlightedDays(endDate);
    // eslint-disable-next-line
  }, [endDate]);

  const handleStartMonthChange = useCallback((date) => {
    fetchHighlightedDays(date);
    // eslint-disable-next-line
  }, []);

  const handleEndMonthChange = useCallback((date) => {
    fetchHighlightedDays(date);
    // eslint-disable-next-line
  }, []);

  const renderInsightBadge = (title, value) => (
    <div className="bg-white p-3 rounded-lg border border-gray-200">
      <Typography variant="body2" color="textSecondary">
        {title}
      </Typography>
      <Typography variant="h6" className="flex items-center gap-1">
        {value}
      </Typography>
    </div>
  );

  return (
    <Card className="shadow-sm">
      <CardContent>
        <div className="flex justify-between items-center mb-4 flex-col md:flex-row text-center md:text-start lg:text-start lg:flex-row">
          <div className="mb-4 md:mb-0 md:mr-4">
            <Typography variant="h6" className="font-medium">
              Expiry Trends Analysis
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Track and analyze expiry patterns over time
            </Typography>
          </div>
          <Stack direction="row" spacing={2} alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                onMonthChange={handleStartMonthChange}
                slots={{
                  day: (props) => (
                    <ServerDay {...props} highlightedDays={highlightedDays} />
                  ),
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
                maxDate={endDate}
              />
              <Typography variant="body2" color="text.secondary">
                to
              </Typography>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                onMonthChange={handleEndMonthChange}
                slots={{
                  day: (props) => (
                    <ServerDay {...props} highlightedDays={highlightedDays} />
                  ),
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
                minDate={startDate}
              />
            </LocalizationProvider>
          </Stack>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          {renderInsightBadge("Total Expired", loading ? <CircularProgress size={20} /> : totalExpired)}
          {renderInsightBadge("Total Nearing Expiry", loading ? <CircularProgress size={20} /> : totalNearExpiry)}
        </div>

        <div style={{ width: '100%', height: 400 }}>
          {loading ? (
            <Box 
              sx={{ 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.8)'
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                margin={{
                  top: 10,
                  right: 5,
                  left: 0,
                  bottom: 30,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  name="Expired Products"
                  dataKey="expired"
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  name="Near Expiry"
                  dataKey="nearExpiry"
                  fill="#f97316"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ExpiryTrendChart;
